import { useEffect, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import AiUsageBadge from './AiUsageBadge';
import MoodPicker from './MoodPicker';

type Project = { id: number; name: string; theme_color: string };

export default function NavPanel() {
  const { user, logout } = useAuth();
  const params = useParams<{ id?: string }>();
  const activeProject = params.id ? Number(params.id) : null;
  const [projects, setProjects] = useState<Project[]>([]);

  useEffect(() => {
    async function load() {
      try {
        const r = await api.get<{ projects: Project[] }>('/projects');
        setProjects(r.projects);
      } catch {
        setProjects([]);
      }
    }
    load();
    // Rafraîchit automatiquement sur événement global
    const onChange = () => load();
    window.addEventListener('projects:changed', onChange);
    return () => window.removeEventListener('projects:changed', onChange);
  }, [activeProject]);

  return (
    <nav className="nav-panel">
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 13, color: 'var(--c-muted)' }}>Bienvenue</div>
        <div style={{ fontWeight: 600 }}>{user?.name}</div>
      </div>

      <h2>Général</h2>
      <NavLink to="/" end className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
        ⌂ Dashboard
      </NavLink>
      <NavLink
        to="/projets"
        end
        className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
      >
        ▦ Tous les projets
      </NavLink>
      <NavLink
        to="/ressources"
        className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
      >
        ☰ Ressources
      </NavLink>
      <NavLink
        to="/perso"
        className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
      >
        ✎ Espace perso
      </NavLink>

      <h2>Projets</h2>
      {projects.length === 0 && (
        <div style={{ color: 'var(--c-muted)', padding: '4px 10px', fontSize: 13 }}>
          Aucun projet
        </div>
      )}
      {projects.map((p) => {
        const isActive = p.id === activeProject;
        return (
          <div key={p.id}>
            <NavLink
              to={`/projets/${p.id}/documents`}
              className={`nav-item ${isActive ? 'active' : ''}`}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 2,
                  background: p.theme_color ?? 'var(--c-sage)',
                }}
              />
              {p.name}
            </NavLink>
            {isActive && (
              <div style={{ marginLeft: 14, marginBottom: 6 }}>
                <NavLink
                  to={`/projets/${p.id}/documents`}
                  className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                >
                  • Documents
                </NavLink>
                <NavLink
                  to={`/projets/${p.id}/taches`}
                  className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                >
                  • Tâches
                </NavLink>
                <NavLink
                  to={`/projets/${p.id}/communication`}
                  className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                >
                  • Communication
                </NavLink>
                <NavLink
                  to={`/projets/${p.id}/perso`}
                  className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                >
                  • Espace perso
                </NavLink>
                <NavLink
                  to={`/projets/${p.id}/parametres`}
                  className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                >
                  • Paramètres
                </NavLink>
              </div>
            )}
          </div>
        );
      })}

      {user?.role === 'admin' && (
        <>
          <h2>Administration</h2>
          <NavLink
            to="/admin/utilisateurs"
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
          >
            ⚙ Utilisateurs
          </NavLink>
          <NavLink
            to="/admin/ia"
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
          >
            ⚙ Configuration IA
          </NavLink>
          <NavLink
            to="/admin/ia/consommation"
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
          >
            ⚙ Consommation IA
          </NavLink>
        </>
      )}

      <div style={{ flex: 1 }} />
      <div style={{ marginBottom: 10 }}>
        <AiUsageBadge adminLink={user?.role === 'admin'} />
      </div>
      <MoodPicker />
      <div style={{ marginTop: 12 }}>
        <button className="btn btn-ghost" style={{ width: '100%' }} onClick={logout}>
          Se déconnecter
        </button>
      </div>
    </nav>
  );
}
